import DOMPurify from 'dompurify'

const ALLOWED_TAGS = ['a', 'strong', 'em', 'ul', 'ol', 'li', 'p', 'br']
const ALLOWED_ATTR = ['href', 'target', 'rel']

type DomPurifyInstance = ReturnType<typeof DOMPurify>

let purifier: DomPurifyInstance | null = null
let hooksRegistered = false

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

const ensurePurifier = () => {
  if (purifier || typeof window === 'undefined') {
    return purifier
  }

  purifier = DOMPurify(window)

  if (!hooksRegistered) {
    hooksRegistered = true
    purifier.addHook('afterSanitizeAttributes', (node: Element) => {
      if (node.nodeName === 'A') {
        const href = node.getAttribute('href') || ''
        if (!href.startsWith('http://') && !href.startsWith('https://')) {
          node.removeAttribute('href')
        }
        node.setAttribute('rel', 'noopener noreferrer')
        node.setAttribute('target', '_blank')
      }

      if (node.hasAttribute && node.hasAttribute('style')) {
        node.removeAttribute('style')
      }
    })
  }

  return purifier
}

export const sanitizeHtml = (value?: string | null) => {
  if (!value) return ''

  const instance = ensurePurifier()
  if (!instance) {
    return escapeHtml(value)
  }

  return instance.sanitize(value, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    FORBID_ATTR: ['style'],
    FORBID_TAGS: ['style'],
    RETURN_DOM: false,
  }) as string
}

export const sanitizeToPlainText = (value?: string | null) => {
  const sanitized = sanitizeHtml(value)
  if (!sanitized) {
    return ''
  }

  return sanitized
    .replace(/<\/?[^>]+(>|$)/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}
